import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { customEasing } from '../lib/easing';

function Navigation({ activeSection }: { activeSection: string }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'research', label: 'Research' },
    { id: 'team', label: 'Team' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <>
      <nav
        aria-label="Primary"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-sm shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="container-asi">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.button
              onClick={() => scrollToSection('home')}
              className="flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label="AI Studies Institute - Home"
            >
              <img 
                src="/logo.png" 
                alt="ASI Logo" 
                className="h-8 w-8 md:h-10 md:w-10 object-contain"
              />
              <span className="font-serif text-lg md:text-xl text-asi-navy font-medium tracking-tight hidden sm:block">
                ASI
              </span>
            </motion.button>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative text-sm font-medium tracking-wide uppercase transition-colors ${
                    activeSection === item.id
                      ? 'text-asi-navy'
                      : 'text-asi-navy/70 hover:text-asi-navy'
                  }`}
                  aria-current={activeSection === item.id ? 'page' : undefined}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute -bottom-1 left-0 right-0 h-0.5 bg-asi-copper"
                      initial={false}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Mobile Hamburger Button */}
            <motion.button
              className="md:hidden p-2 -mr-2 text-asi-navy"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileTap={{ scale: 0.9 }}
              aria-label="Toggle menu"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <motion.span
                  className="w-full h-0.5 bg-current origin-left"
                  animate={{
                    rotate: isMobileMenuOpen ? 45 : 0,
                    y: isMobileMenuOpen ? 0 : 0,
                  }}
                  transition={{ duration: 0.3, ease: customEasing.smooth }}
                />
                <motion.span
                  className="w-full h-0.5 bg-current"
                  animate={{
                    opacity: isMobileMenuOpen ? 0 : 1,
                    scaleX: isMobileMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.3, ease: customEasing.smooth }}
                />
                <motion.span
                  className="w-full h-0.5 bg-current origin-left"
                  animate={{
                    rotate: isMobileMenuOpen ? -45 : 0,
                    y: isMobileMenuOpen ? 0 : 0,
                  }}
                  transition={{ duration: 0.3, ease: customEasing.smooth }}
                />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Reading Progress Bar */}
        <div className="h-px bg-gray-100">
          <div
            id="reading-progress"
            className="h-full bg-asi-copper transition-all duration-100"
            style={{ width: '0%' }}
          />
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/30 z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              className="fixed top-0 right-0 bottom-0 w-[280px] bg-white z-50 md:hidden shadow-2xl"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: customEasing.smooth }}
            >
              <div className="flex flex-col h-full">
                {/* Drawer Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <span className="font-serif text-lg text-asi-navy">Menu</span>
                  <motion.button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 -mr-2 text-asi-navy/60 hover:text-asi-navy"
                    whileTap={{ scale: 0.9 }}
                    aria-label="Close menu"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                </div>

                {/* Navigation Items */}
                <nav className="flex-1 p-6" aria-label="Mobile">
                  <ul className="space-y-2">
                    {navItems.map((item, index) => (
                      <motion.li
                        key={item.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: index * 0.08,
                          duration: 0.3,
                          ease: customEasing.smooth,
                        }}
                      >
                        <motion.button
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full text-left px-4 py-4 rounded-lg text-lg font-medium transition-all ${
                            activeSection === item.id
                              ? 'bg-asi-navy text-white'
                              : 'text-asi-navy hover:bg-gray-50'
                          }`}
                          aria-current={activeSection === item.id ? 'page' : undefined}
                          whileTap={{ scale: 0.98 }}
                        >
                          {item.label}
                        </motion.button>
                      </motion.li>
                    ))}
                  </ul>
                </nav>

                {/* Drawer Footer */}
                <div className="p-6 border-t border-gray-100">
                  <a
                    href="mailto:contact@aistudies.institute"
                    className="text-sm text-asi-navy/60 hover:text-asi-copper transition-colors"
                  >
                    contact@aistudies.institute
                  </a>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}


export default Navigation;
